import { parseISO, format } from 'date-fns'
import {
    WCC_TEMPLATE_CURRENCY_DISPLAY_FORMAT,
    WCC_TEMPLATE_MOBILE_DISPLAY_FORMAT,
    WCC_TEMPLATE_NUMBER_FIVE_DECIMALS_FORMAT,
    WCC_TEMPLATE_NUMBER_THREE_DECIMALS_FORMAT,
    WCC_TEMPLATE_NUMBER_THREE_DECIMALS_PERCENT_FORMAT,
    WCC_TEMPLATE_NUMBER_TWO_DECIMALS_FORMAT,
    WCC_TEMPLATE_PROPERCASE_DISPLAY_FORMAT,
    WCC_TEMPLATE_SSN_DISPLAY_FORMAT,
    WCC_TEMPLATE_UPPERCASE_DISPLAY_FORMAT,
} from './constants'

export interface FormatResult {
    isValid: boolean
    value: string
}

export const formatTo = (value: string = '', format: string): FormatResult => {
    const r: FormatResult = {
        isValid: false,
        value: value, // default is the value. Caller shall decide with the value.
    }
    switch (format) {
        case WCC_TEMPLATE_CURRENCY_DISPLAY_FORMAT: {
            // value with alphabets is invalid.
            if (/[a-zA-Z]/.test(value)) {
                r.isValid = false
                r.value = value
                return r
            }
            // extract the number value
            const numberValue =
                typeof value !== 'number'
                    ? value
                          .replace(/[^0-9.-]+/g, '')
                          .match(/^[-+]?[0-9]*\.?[0-9]+$/)
                        ? parseFloat(
                              value.replace(/[^0-9.-]+/g, '').replace(/,/g, '')
                          )
                        : NaN
                    : value
            if (!isNaN(numberValue)) {
                const v: string = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(numberValue)
                r.isValid = true
                r.value = v
            }
            return r
        }
        case WCC_TEMPLATE_NUMBER_TWO_DECIMALS_FORMAT: {
            const v: number = Number(value)
            if (!isNaN(v)) {
                const rounded = Number(v.toFixed(2)) // Ensure 2 digits to inspect
                const [, decimal = ''] = rounded.toString().split('.')
                if (decimal.length < 2 || decimal[1] === '0') {
                    r.isValid = true
                    r.value = parseFloat(v.toFixed(1)).toString()
                } else {
                    r.isValid = true
                    r.value = parseFloat(v.toFixed(2)).toString()
                }
            }
            return r
        }
        case WCC_TEMPLATE_NUMBER_THREE_DECIMALS_FORMAT: {
            const v: number = Number(value)
            if (!isNaN(v)) {
                const rounded = Number(v.toFixed(3)) // Ensure 3 digits to inspect
                const [, decimal = ''] = rounded.toString().split('.')
                if (decimal.length < 3 || decimal[2] === '0') {
                    r.isValid = true
                    r.value = parseFloat(v.toFixed(2)).toString()
                } else {
                    r.isValid = true
                    r.value = parseFloat(v.toFixed(3)).toString()
                }
            }
            return r
        }
        case WCC_TEMPLATE_NUMBER_FIVE_DECIMALS_FORMAT: {
            const v: number = Number(value)
            if (!isNaN(v)) {
                const rounded = Number(v.toFixed(5)) // Ensure 3 digits to inspect
                const [, decimal = ''] = rounded.toString().split('.')
                if (decimal.length < 5 || decimal[4] === '0') {
                    r.isValid = true
                    r.value = parseFloat(v.toFixed(4)).toString()
                } else {
                    r.isValid = true
                    r.value = parseFloat(v.toFixed(5)).toString()
                }
            }
            return r
        }
        case WCC_TEMPLATE_NUMBER_THREE_DECIMALS_PERCENT_FORMAT: {
            const v: number = Number(
                value.endsWith('%')
                    ? value.substring(0, value.length - 1)
                    : value
            )
            if (!isNaN(v)) {
                const rounded = Number(v.toFixed(3)) // Ensure 3 digits to inspect
                const [, decimal = ''] = rounded.toString().split('.')
                if (decimal.length < 3 || decimal[2] === '0') {
                    r.isValid = true
                    r.value = `${parseFloat(v.toFixed(2)).toString()}%`
                } else {
                    r.isValid = true
                    r.value = `${parseFloat(v.toFixed(3)).toString()}%`
                }
            }
            return r
        }
        case WCC_TEMPLATE_PROPERCASE_DISPLAY_FORMAT: {
            let words: string[] = value.split(' ')
            words = words.map(
                (word: string) =>
                    word.charAt(0).toUpperCase() +
                    word.substring(1, word.length).toLowerCase()
            )
            r.isValid = true
            r.value = words.join(' ')
            return r
        }
        case WCC_TEMPLATE_UPPERCASE_DISPLAY_FORMAT: {
            r.isValid = true
            r.value = value.toUpperCase()
            return r
        }
        case WCC_TEMPLATE_SSN_DISPLAY_FORMAT: {
            if (value && value.length === 9) {
                r.isValid = true
                r.value = `${value.substring(0, 3)}-${value.substring(
                    3,
                    5
                )}-${value.substring(5, 9)}`
            }
            return r
        }
        case WCC_TEMPLATE_MOBILE_DISPLAY_FORMAT: {
            // If already formatted, return the same value
            const pattern = /^\(\d{3}\) \d{3}-\d{4}$/
            if (pattern.test(value)) {
                r.isValid = true
            }
            if (value && value.length === 10) {
                r.isValid = true
                r.value = `(${value.slice(0, 3)}) ${value.slice(
                    3,
                    6
                )}-${value.slice(6)}`
            }
            return r
        }
        default: {
            r.isValid = true
        }
    }
    return r
}

/**
 * Converts a UTC ISO date string to a formatted local time string.
 *
 * @param {string} utcString - A UTC ISO date string (e.g., "2025-05-20T17:37:03.803Z").
 * @param {string} [formatStr='MM/dd/yyyy hh:mm a'] - Optional date-fns format string.
 * @returns {string} Formatted date string in the user's local timezone.
 */
export const dateFormatter = (
    utcString: string,
    formatStr: string = 'MM/dd/yyyy hh:mm a'
): string => {
    if (!utcString) return ''

    const utcDate = parseISO(utcString) // Parses ISO 8601 string into a Date object
    return format(utcDate, formatStr) // Formats using local time
}

/**
 * Custom deep merge function to deep merge the nested objects and replace
 * the keys from source.
 * @param {*} target
 * @param {*} source
 * @returns
 */
export function deepMergeNestedKeys(target: any, source: any): any[] {
    if (Array.isArray(source)) {
        // Replace arrays completely
        return source.slice()
    } else if (typeof source === 'object' && source !== null) {
        if (
            typeof target !== 'object' ||
            target === null ||
            Array.isArray(target)
        ) {
            target = {}
        }

        for (const key in source) {
            if (source.hasOwnProperty(key)) {
                if (
                    typeof source[key] === 'object' &&
                    source[key] !== null &&
                    !Array.isArray(source[key])
                ) {
                    target[key] = deepMergeNestedKeys(target[key], source[key])
                } else {
                    target[key] = source[key]
                }
            }
        }

        return target
    } else {
        return source
    }
}

export function parseCurrency(value: string): number {
    return parseFloat(value.replace(/[^0-9.-]+/g, ''))
}

export function generateShortNumericId(): string {
    const time = (Date.now() % 1e6).toString().padStart(6, '0') // last 6 digits of timestamp
    const rand = Math.floor(Math.random() * 90 + 10) // 2-digit random
    return `${time}${rand}` // total 8 digits
}

export function getInitials(name: string): string {
    if (!name) return ''
    const nameArray: string[] = name.split(' ')
    const initials = nameArray.map((n) => n.charAt(0).toLowerCase()).join('')
    return initials
}

export function isProduction(): boolean {
    return process.env.NODE_ENV === 'production'
}

export function isDevelopment(): boolean {
    return process.env.NODE_ENV === 'development'
}

export function isReload(): boolean {
    const navEntries = performance.getEntriesByType(
        'navigation'
    ) as PerformanceNavigationTiming[]
    return navEntries.length > 0 && navEntries[0].type === 'reload'
}

export const toComma = (arr?: (string | number | null | undefined)[]) =>
    (arr ?? [])
        .filter((v): v is string | number => v != null && `${v}`.trim() !== '')
        .join(', ')

export const capitalizeFirst = (s?: string | null) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : ''

/**
 * Validates if a date string is valid
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if valid date
 */
export function isValidDate(dateString: string): boolean {
    if (!dateString) return false
    const date = new Date(dateString)
    return !isNaN(date.getTime())
}

/**
 * Checks if a date is in the future
 * @param {string} dateString - Date string to check
 * @returns {boolean} True if date is in the future
 */
export function isFutureDate(dateString: string): boolean {
    if (!dateString) return false
    const date = new Date(dateString)
    const today = new Date()
    today.setHours(23, 59, 59, 999)
    return date.getTime() > today.getTime()
}

/**
 * Validates date range (start date must be before or equal to end date)
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @returns {boolean} True if valid range
 */
export function isValidDateRange(startDate: string, endDate: string): boolean {
    if (!startDate || !endDate) return true // Allow partial ranges
    const start = new Date(startDate)
    const end = new Date(endDate)
    return start.getTime() <= end.getTime()
}

/**
 * Gets the number of days between two dates
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @returns {number} Number of days between dates
 */
export function getDaysBetween(startDate: string, endDate: string): number {
    const start = new Date(startDate).getTime()
    const end = new Date(endDate).getTime()
    return Math.floor((end - start) / (1000 * 60 * 60 * 24))
}

/**
 * Validates if date range exceeds maximum allowed days
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @param {number} maxDays - Maximum allowed days (default: 365)
 * @returns {boolean} True if range is within limit
 */
export function isDateRangeWithinLimit(
    startDate: string,
    endDate: string,
    maxDays: number = 365
): boolean {
    if (!startDate || !endDate) return true
    const days = getDaysBetween(startDate, endDate)
    return days <= maxDays
}

/**
 * Sets time to end of day (23:59:59.999)
 * @param {string} dateString - Date string
 * @returns {Date} Date object set to end of day
 */
export function getEndOfDay(dateString: string): Date {
    const date = new Date(dateString)
    date.setHours(23, 59, 59, 999)
    return date
}

/**
 * Sets time to start of day (00:00:00.000)
 * @param {string} dateString - Date string
 * @returns {Date} Date object set to start of day
 */
export function getStartOfDay(dateString: string): Date {
    const date = new Date(dateString)
    date.setHours(0, 0, 0, 0)
    return date
}
